<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Cms/Element/CmsElementBuyBox.vue
// IDS-160: add vendor link
import type { CmsElementBuyBox } from '@shopware-pwa/composables-next';
import { getTranslatedProperty } from '@shopware-pwa/helpers-next';

const props = defineProps<{
    element: CmsElementBuyBox;
}>();

const { product } = useProduct(props.element.data.product);

const { t } = useI18n();
const customerStore = useCustomerStore();
const { signedIn } = storeToRefs(customerStore);
const showAddToCartButton = computed(() => {
    return signedIn.value  || (product.value?.customFields?.priceTransparency === 'SHOW_PRICE' && product.value?.translated.customFields?.netPrice);
});
const configStore = useConfigStore();
const wishlistEnabled = configStore.get('core.cart.wishlistEnabled');
const route = useRoute();
const { getCountrySpecificVendor } = useVendor();
const countrySpecificVendor = getCountrySpecificVendor(product.value?.extensions?.vendors);
const { isAddToCartAvailable } = useAddToCartAvailability(product.value);
</script>

<template>
    <div class="relative flex min-h-full flex-wrap justify-between">
        <!-- IDS-84 - add product title and manufacturer -->
        
<FormKitLazyProvider config-file="true">
<div class="w-full">
            <div class="grid w-full flex-row justify-between md:flex">
                <h1 class="order-1 mt-4 text-3xl font-black leading-9 md:order-[unset] md:mt-0">
                    {{ getTranslatedProperty(product, 'name') }}
                </h1>
                <ProductManufacturer :manufacturer="product.manufacturer" />
            </div>

            <!-- IDS-84 - add product number -->
            <span
                v-if="product?.productNumber"
                class="mb-4 mt-2 block text-neutral-800"
            >
                {{ t('productIds.itemNumber') }}&nbsp;{{ product.productNumber }}
            </span>

            <!-- eslint-disable vue/no-v-html -->
            <div
                id="product-description"
                class="mb-4"
                v-html="getTranslatedProperty(product.customFields, 'product_short_description')"
            />

            <div class="mb-4">
                <ProductVariantSelection :product="product" />
            </div>
        </div>

        <!-- IDS-84 - change price display and add shipping info -->
        <div class="h-fit w-full place-self-end">
            <div class="bg-neutral-300 p-6">
                <!--CUSTOM IDS-151: Check if product is unavailable-->
                <div v-if="isAddToCartAvailable">
                    <ProductPrice
                        :product="product"
                        :display-long-vat="true"
                        :display-price-info="true"
                        font-size="text-3xl"
                    >
                        <template #priceHidden>
                            <span class="text-3xl font-extrabold">
                                {{ t('global.price.loginNeeded.buybox') }}
                            </span>
                        </template>
                    </ProductPrice>

                    <div
                        v-if="countrySpecificVendor && showAddToCartButton"
                        class="mt-1 flex gap-1 text-sm text-neutral-600"
                    >
                        {{ t('account.orders.shippingInfoShort') }}
                        <SharedVendorHint :vendor="countrySpecificVendor" />
                    </div>

                    <div
                        v-if="countrySpecificVendor.shippingMethod.active"
                        class="before-bottom-0 relative mt-2 pl-4 text-sm text-accent-green before:absolute before:left-0 before:top-0 before:my-1 before:size-3 before:rounded-full before:bg-accent-green before:content-['']"
                    >
                        {{
                            t('productIds.shipping.available', {
                                deliveryTime: getTranslatedProperty(
                                    countrySpecificVendor.shippingMethod.deliveryTime,
                                    'name',
                                ),
                            })
                        }}
                    </div>

                    <div
                        v-if="showAddToCartButton"
                        class="mt-4 flex w-full flex-row gap-4"
                    >
                        <ProductAddToCartPDP
                            :product="product"
                            :label="true"
                        />
                    </div>
                    <div
                        v-else
                        class="mt-4 flex w-full flex-row gap-4"
                    >
                        <LocaleLink
                            class="w-full"
                            :to="`/account/login?redirect=${route.fullPath}`"
                        >
                            <FormKit
                                type="submit"
                                :classes="{
                            outer: 'w-full',
                            input: '!w-full',
                            wrapper: 'w-full',
                        }"
                                :label="t('global.price.loginNeeded.registerNow')"
                            />
                        </LocaleLink>
                    </div>
                </div>

                <div v-else>
                    <div class="flex min-h-60 flex-col">
                        <p class="text-3xl font-extrabold leading-8.5">{{ t('product.unavailable') }}</p>
                        <p class="pt-2 text-sm font-normal leading-5 text-neutral-600">
                            {{ t('product.unavailability.info') }}
                        </p>
                    </div>
                </div>
            </div>

            <div
                v-if="wishlistEnabled"
                class="mt-6"
            >
                <ProductAddToWishlist
                    :product="product"
                    layout="text"
                />
            </div>
        </div>
</FormKitLazyProvider>

    </div>
</template>

<style scoped>
#product-description >>> ul {
    margin-left: 1rem !important;
    list-style: disc !important;
}
#product-description >>> ol {
    margin-left: 1rem !important;
    list-style: decimal !important;
}
</style>
